/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useState, useRef } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Typography,
    Box,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    TextField,
    useTheme,
    Checkbox,
    Snackbar,
    Alert,
    LinearProgress,
    IconButton,
    DialogTitle,
    Paper,
    Grid,
    Divider,
    CircularProgress,
    Modal,
    alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PixIcon from '@mui/icons-material/QrCode';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { FaturamentoService } from '../../shared/services/api/Venda/Fiscal';
import { VendasService } from '../../shared/services/api/Venda/venda';
import * as yup from 'yup';
import { PDVThemeColors } from './PDVThemes';
import { SpinningLogo } from '../../shared/components';

export interface IPagamentoData {
    id: string;
    cpf: string;
    idconta: string;
    faturado: boolean;
    nome: string;
    indpag: number;
    tpag: string;
    tcnpj?: string;
    tband?: string;
    caut?: string;
    promocao: boolean;
}

export interface PaymentDialogProps {
    open: boolean;
    selectedId: string | null;
    onClose: (value?: string) => void;
    incluirCpf: boolean;
    colors: PDVThemeColors;
    // Categoria da forma de pagamento usada na venda (ver categoriaEfetiva em
    // Dialogvenda.tsx: 'Dinheiro', 'Cartão Crédito', 'Cartão Débito', 'PIX
    // Estático', 'PIX Dinâmico'...) — pré-seleciona a mesma forma aqui (ver
    // TPAG_POR_CATEGORIA abaixo), pro operador não escolher de novo algo que
    // já escolheu na venda. Categoria não reconhecida cai em "Sem Pagamento".
    formaPagamentoVenda?: string;
    // true pra orçamento — orçamento não é uma venda de verdade, não tem
    // forma de pagamento física real, então só sugere Dinheiro como ponto de
    // partida (ver getInitialPagamento abaixo). NUNCA trava/desabilita o
    // campo — mesmo em orçamento, o operador tem que poder trocar livremente,
    // a pedido.
    isOrcamento?: boolean;
    // Mensagens de sucesso vão pro header do PDV (ver mostrarMensagem em
    // Pdv.tsx) em vez do Snackbar flutuante — erro/aviso continuam aqui.
    onMensagemSucesso?: (texto: string) => void;
}

// Funções de validação
const isValidCPF = (cpf: string): boolean => {
    if (!cpf) return false;

    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11) return false;
    if (/^(\d)\1+$/.test(cleanCPF)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let remainder = 11 - (sum % 11);
    let digit = remainder === 10 || remainder === 11 ? 0 : remainder;
    if (digit !== parseInt(cleanCPF.charAt(9))) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    remainder = 11 - (sum % 11);
    digit = remainder === 10 || remainder === 11 ? 0 : remainder;
    if (digit !== parseInt(cleanCPF.charAt(10))) return false;

    return true;
};

const formatCPF = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
};

// Mesmo mapeamento que o backend usa (CATEGORIA_POR_TPAG, ver
// ContasRecebiveisFixas.ts), invertido — categoria da venda (como o PDV
// grava, ver categoriaEfetiva em Dialogvenda.tsx) -> código de forma de
// pagamento da NFC-e. Categoria não reconhecida (ex.: venda a prazo/
// faturada, sem forma de pagamento física de verdade) cai em Sem Pagamento.
const TPAG_POR_CATEGORIA: Record<string, string> = {
    'Dinheiro': '01',
    'Cartão Crédito': '03',
    'Cartão Débito': '04',
    'PIX Dinâmico': '17',
    'PIX Estático': '20',
};

const mapearCategoriaParaTpag = (categoria?: string): string => TPAG_POR_CATEGORIA[categoria || ''] || '90';

// Cor fixa (não vem do tema, a pedido) pro contraste mais forte de
// Bandeira/CAUT em "Detalhes do Cartão" — esses dois campos ficam bem
// visíveis independente da cor de tema escolhida no PDV.
const COR_CONTRASTE_CAMPO = '#1E293B';

const createPagamentoSchema = (incluirCpf: boolean) => {
    return yup.object().shape({
        id: yup.string().required('O ID é obrigatório'),
        indpag: yup.number().required('Informe o tipo de pagamento'),
        tpag: yup.string().required('Selecione a forma de pagamento'),
        cpf: yup.string().test('cpf-validation', 'CPF inválido', function(value) {
            if (!incluirCpf) return true;
            if (!value || value.trim() === '') return false;
            return isValidCPF(value);
        }),
        nome: yup.string().test('nome-validation', 'Nome é obrigatório', function(value) {
            if (!incluirCpf) return true;
            if (!value || value.trim() === '') return false;
            return true;
        }),
        tband: yup.string().when('tpag', {
            is: (tpag: string) => tpag === '03' || tpag === '04',
            then: (schema) => schema.required('Bandeira é obrigatória'),
            otherwise: (schema) => schema.optional()
        }),
        caut: yup.string().when('tpag', {
            is: (tpag: string) => tpag === '03' || tpag === '04',
            then: (schema) => schema.required('CAUT é obrigatório').min(6, 'Mínimo 6 dígitos'),
            otherwise: (schema) => schema.optional()
        }),
        tcnpj: yup.string().optional().default(''),
        idconta: yup.string().optional().default(''),
        promocao: yup.boolean().required(),
    });
};

// Componente de Loading de Tela Cheia com mensagens progressivas — filho do
// próprio Paper do Dialog de emitir NFC-e (que tem position:relative, ver
// PaperProps mais abaixo), não um Backdrop solto por fora dele. Um Backdrop
// separado, mesmo com z-index bem alto, ainda dependia de empilhamento entre
// dois elementos diferentes — na prática continuava renderizando atrás do
// Dialog às vezes (mesmo problema já corrigido em Dialogvenda.tsx). Sendo
// filho do mesmo Paper, pinta por cima de tudo que vem antes dele ali
// dentro, sem depender de z-index nem de portal nenhum.
const FullScreenLoading: React.FC<{ message?: string; progress?: number }> = ({
    message = "Emitindo nota fiscal...",
    progress
}) => {
    return (
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 20,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3
            }}
        >
            <SpinningLogo />
            <Typography
                variant="h6"
                sx={{
                    color: 'white',
                    fontWeight: 500,
                    textAlign: 'center'
                }}
            >
                {message}
            </Typography>
            {progress !== undefined && progress > 0 && (
                <Box sx={{ width: '300px', mt: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'white'
                            }
                        }}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, display: 'block', textAlign: 'center' }}>
                        {Math.round(progress)}%
                    </Typography>
                </Box>
            )}
            <Typography
                variant="body2"
                sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center'
                }}
            >
                Aguarde enquanto processamos sua solicitação...
            </Typography>
        </Box>
    );
};

// Componente de Visualização de Impressão
const PrintPreviewModal: React.FC<{
    open: boolean;
    pdfBlob: Blob | null;
    onClose: () => void;
    onPrint: () => void;
}> = ({ open, pdfBlob, onClose, onPrint }) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        if (pdfBlob && open) {
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(url);

            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [pdfBlob, open]);

    const handlePrint = () => {
        if (iframeRef.current) {
            try {
                const iframe = iframeRef.current;
                const iframeWindow = iframe.contentWindow;
                if (iframeWindow) {
                    iframeWindow.focus();
                    iframeWindow.print();
                }
            } catch (error) {
                console.error('Erro ao imprimir:', error);
                onPrint();
            }
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="print-preview-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: (theme) => theme.zIndex.modal + 1
            }}
        >
            <Paper
                sx={{
                    width: '90%',
                    height: '90%',
                    maxWidth: '1200px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 2
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'background.paper'
                    }}
                >
                    <Typography variant="h6" fontWeight="600">
                        <PictureAsPdfIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Visualização da NFC-e
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            sx={{ textTransform: 'none' }}
                        >
                            Imprimir
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ textTransform: 'none' }}
                        >
                            Fechar
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f5f5f5' }}>
                    {pdfUrl ? (
                        <iframe
                            ref={iframeRef}
                            src={pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            title="Pré-visualização da NFC-e"
                        />
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Modal>
    );
};

export const Dialogsendnfce: React.FC<PaymentDialogProps> = (props) => {
    const { onClose, open, selectedId, colors, formaPagamentoVenda, isOrcamento } = props;
    const theme = useTheme();
    const corFundoSuave = theme.palette.mode === 'light' ? '#F3F7FB' : 'rgba(255,255,255,0.04)';
    const corFundoCard = theme.palette.mode === 'light' ? '#f8f9fa' : 'rgba(255,255,255,0.05)';

    const getInitialPagamento = useCallback((): IPagamentoData => ({
        id: selectedId || '',
        indpag: 0,
        faturado: true,
        idconta: '',
        // Orçamento não tem forma de pagamento física real — Dinheiro só
        // como sugestão inicial, o campo continua editável normalmente (ver
        // FormControl mais abaixo, sem disabled nenhum).
        tpag: isOrcamento ? '01' : mapearCategoriaParaTpag(formaPagamentoVenda),
        cpf: '',
        nome: '',
        tband: '',
        caut: '',
        tcnpj: '',
        promocao: false,
    }), [selectedId, formaPagamentoVenda, isOrcamento]);

    const [isLoading, setIsLoading] = useState(false);
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const [pagamento, setPagamento] = useState<IPagamentoData>(getInitialPagamento);
    const [tbandError, setTbandError] = useState<string>('');
    const [cautError, setCautError] = useState<string>('');

    // Resumo da venda (número/valor) pro topo do modal — buscado pelo próprio
    // id, já que quem abre esse dialog (Dialogvenda.tsx) só passa o id, não
    // os dados formatados (esse dialog é reaproveitado em dois pontos: direto
    // depois da venda, e pelo botão "Emitir nota" dentro da tela de
    // visualizar/reimprimir — ver VisualizarPDF.tsx).
    const [vendaResumo, setVendaResumo] = useState<{ numero: string; total: number } | null>(null);

    // Dialog "Informar CPF do cliente" — rascunho próprio (cpfDraft/nomeDraft)
    // que só vira o CPF/nome de verdade do pagamento ao clicar Salvar; fechar
    // sem salvar (Cancelar/X/ESC) descarta o rascunho, como pedido.
    const [openCpfDialog, setOpenCpfDialog] = useState(false);
    const [cpfDraft, setCpfDraft] = useState('');
    const [nomeDraft, setNomeDraft] = useState('');
    const [cpfDraftError, setCpfDraftError] = useState('');
    const [nomeDraftError, setNomeDraftError] = useState('');

    const [openmsg, setOpenmsg] = useState(false);
    const [msg, setMsg] = useState('');
    const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('info');
    // Erro específico de EMITIR a nota (falha ao chamar o backend/SEFAZ,
    // ver catch de handleConfirmarPagamento) — separado do Snackbar comum
    // (CPF/bandeira/CAUT) porque só esse precisa abrir flutuando por cima
    // do diálogo (ver Alert logo depois do PaperProps, mais abaixo).
    const [erroEmissao, setErroEmissao] = useState('');

    const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [loadingMessage, setLoadingMessage] = useState('Emitindo nota fiscal...');
    const [loadingProgress, setLoadingProgress] = useState(0);

    // Ref para controle de timeout
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);

    // Erro só some quando o operador clica em fechar de verdade — nem
    // sozinho por tempo (ver autoHideDuration do Snackbar abaixo, que não
    // se aplica a erro) nem clicando fora sem querer (reason 'clickaway').
    const handleCloseMsg = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setOpenmsg(false);
    };

    // Sucesso não flutua na tela: vai pro header do PDV (onMensagemSucesso).
    const sucessoNoHeader = tms === 'success' && !!props.onMensagemSucesso;
    useEffect(() => {
        if (openmsg && sucessoNoHeader) {
            props.onMensagemSucesso?.(msg);
            setOpenmsg(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openmsg, sucessoNoHeader, msg]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedId) {
            setPagamento((prev) => ({ ...prev, id: selectedId }));
        }
    }, [selectedId, open]);

    // Resumo (número/valor) da venda — só busca com o modal aberto, ignora
    // resposta se o dialog fechar/trocar de venda no meio do caminho.
    useEffect(() => {
        if (!open || !selectedId) { setVendaResumo(null); return; }
        let cancelado = false;
        VendasService.Impressao(selectedId).then((resultado) => {
            if (cancelado || resultado instanceof Error) return;
            setVendaResumo({ numero: resultado.numero, total: resultado.total });
        });
        return () => { cancelado = true; };
    }, [open, selectedId]);

    useEffect(() => {
        if (open) {
            setPagamento(getInitialPagamento());
            setTbandError('');
            setCautError('');
            setOpenCpfDialog(false);
            setCpfDraft('');
            setNomeDraft('');
            setCpfDraftError('');
            setNomeDraftError('');
            setShowPrintPreview(false);
            setPdfBlob(null);
            setShowFullScreenLoading(false);
            setLoadingProgress(0);
            // Erro/mensagem de uma emissão anterior (venda diferente ou
            // tentativa anterior da mesma venda) não pode continuar
            // aparecendo quando o diálogo reabre — cada abertura começa
            // limpa, só guarda o que acontecer NESTA emissão.
            setOpenmsg(false);
            setMsg('');
            setTms('info');
            setErroEmissao('');
            // Limpar timeout ao abrir
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    }, [open, selectedId, getInitialPagamento]);

    // Refs sempre atualizadas (logo abaixo da declaração de cada handler)
    // com as versões mais recentes, para o listener de teclado não precisar
    // de um array de deps gigante nem correr risco de invocar closures desatualizadas.
    const handleCloseAttemptRef = useRef<() => void>(() => undefined);
    const handleConfirmCloseRef = useRef<() => void>(() => undefined);
    const handleConfirmarPagamentoRef = useRef<(dados: IPagamentoData) => Promise<void>>(async () => undefined);

    // Adicionar listener para tecla F12
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Dialog de CPF ou de confirmação ("Tem certeza...") aberto por
            // cima — F12/ESC agem nele (o próprio MUI Dialog já trata ESC
            // via onClose), não no dialog de trás. Sem esse "return" pro
            // showCloseConfirmation, ESC nessa dialog disparava os DOIS
            // handlers pro mesmo evento: o onClose do MUI (fecha a
            // confirmação) E este listener global (chama handleCloseAttempt
            // de novo, que REABRIA a confirmação na hora, já que
            // showPrintPreview continua false) — na prática, ESC parecia
            // não fazer nada nela.
            if (openCpfDialog || showCloseConfirmation) return;
            // F12 para emitir nota
            if (event.key === 'F12' && open && !showPrintPreview && !showFullScreenLoading) {
                event.preventDefault();
                handleConfirmarPagamentoRef.current(pagamento);
            }
            // ESC sai direto, SEM a confirmação "Tem certeza..." (a pedido):
            // depois da venda, cai numa venda nova na hora (a nota ainda
            // pode ser emitida depois em Notas Fiscais → "Sem nota"). A
            // confirmação continua só no X/botão de cancelar. O Dialog do MUI
            // está com disableEscapeKeyDown, então só este listener trata o
            // ESC (sem fechar duas vezes).
            if (event.key === 'Escape' && open && !showPrintPreview && !showFullScreenLoading) {
                event.preventDefault();
                handleConfirmCloseRef.current();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, showPrintPreview, showFullScreenLoading, openCpfDialog, showCloseConfirmation, pagamento]);

    const todasFormasPagamento = [
        { id: '01', nome: 'Dinheiro', icon: <MoneyIcon sx={{ fontSize: 18 }} /> },
        { id: '20', nome: 'PIX Estático', icon: <PixIcon sx={{ fontSize: 18 }} /> },
        { id: '17', nome: 'PIX Dinâmico', icon: <PixIcon sx={{ fontSize: 18 }} /> },
        { id: '04', nome: 'Cartão de Débito', icon: <CreditCardIcon sx={{ fontSize: 18 }} /> },
        { id: '03', nome: 'Cartão de Crédito', icon: <CreditCardIcon sx={{ fontSize: 18 }} /> },
        { id: '90', nome: 'Sem Pagamento', icon: <PaymentIcon sx={{ fontSize: 18 }} /> },
    ];

    const bandeirasCartao = [
        { id: '01', nome: 'Visa', cor: '#1a1f71' },
        { id: '02', nome: 'Mastercard', cor: '#eb001b' },
        { id: '06', nome: 'Elo', cor: '#00a4e0' },
        { id: '07', nome: 'Hipercard', cor: '#f47b00' },
        { id: '03', nome: 'American Express', cor: '#2e77bc' },
        { id: '99', nome: 'Outros', cor: '#666666' },
    ];

    const resetPagamento = () => {
        setPagamento(getInitialPagamento());
    };

    const isPagamentoCartao = pagamento.tpag === '03' || pagamento.tpag === '04';
    const isPagamentoDinheiro = pagamento.tpag === '01';
    // CPF "informado" = presença de dado, não um toggle à parte — salvar o
    // dialog de CPF é o que liga; cancelar/nunca abrir é o que desliga.
    const cpfNaNotaInformado = !!(pagamento.cpf && pagamento.nome);

    const handleCloseAttempt = () => {
        if (showPrintPreview) {
            handleClosePrintPreview();
        } else {
            setShowCloseConfirmation(true);
        }
    };
    handleCloseAttemptRef.current = handleCloseAttempt;

    const handleConfirmClose = () => {
        // Limpar timeouts
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setShowCloseConfirmation(false);
        setShowPrintPreview(false);
        setPdfBlob(null);
        setShowFullScreenLoading(false);
        resetPagamento();
        onClose();
    };
    handleConfirmCloseRef.current = handleConfirmClose;

    const handleCancelClose = () => setShowCloseConfirmation(false);

    const handleClosePrintPreview = () => {
        setShowPrintPreview(false);
        setPdfBlob(null);
        onClose('success');
    };

    // Dialog "Informar CPF do cliente"
    const handleAbrirCpfDialog = () => {
        setCpfDraft(pagamento.cpf);
        setNomeDraft(pagamento.nome);
        setCpfDraftError('');
        setNomeDraftError('');
        setOpenCpfDialog(true);
    };

    const handleCpfDraftChange = (value: string) => {
        const formattedCpf = formatCPF(value);
        setCpfDraft(formattedCpf);

        if (formattedCpf && formattedCpf.replace(/\D/g, '').length === 11) {
            setCpfDraftError(!isValidCPF(formattedCpf) ? 'CPF inválido' : '');
        } else if (formattedCpf && formattedCpf.replace(/\D/g, '').length > 0) {
            setCpfDraftError('CPF deve conter 11 dígitos');
        } else {
            setCpfDraftError('');
        }
    };

    const handleCancelarCpfDialog = () => setOpenCpfDialog(false);

    const handleSalvarCpfDialog = () => {
        let temErro = false;
        if (!cpfDraft || !isValidCPF(cpfDraft)) {
            setCpfDraftError('CPF inválido');
            temErro = true;
        }
        if (!nomeDraft.trim()) {
            setNomeDraftError('Nome / Razão Social é obrigatório');
            temErro = true;
        }
        if (temErro) return;

        setPagamento((prev) => ({ ...prev, cpf: cpfDraft, nome: nomeDraft }));
        setOpenCpfDialog(false);
    };

    const handleTbandChange = (value: string) => {
        setPagamento({ ...pagamento, tband: value });
        setTbandError(isPagamentoCartao && (!value || value === '') ? 'Selecione a bandeira' : '');
    };

    const handleCautChange = (value: string) => {
        setPagamento({ ...pagamento, caut: value });
        setCautError(isPagamentoCartao && (!value || value.length < 6) ? 'Mínimo 6 dígitos' : '');
    };

    const handlePrint = () => {
        if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        }
    };

    // Função com retry automático
    const fetchWithRetry = async (
        fn: () => Promise<any>,
        retries: number = 2,
        delay: number = 1000
    ): Promise<any> => {
        try {
            return await fn();
        } catch (error: any) {
            // Verifica se é erro de timeout ou de rede
            const isNetworkError = error?.message?.includes('timeout') ||
                                   error?.message?.includes('NetworkError') ||
                                   error?.name === 'TimeoutError' ||
                                   error?.code === 'ECONNABORTED' ||
                                   error?.message?.includes('Failed to fetch');

            if (retries > 0 && isNetworkError && isMountedRef.current) {
                setLoadingMessage(`Tentativa ${3 - retries + 1} de 3...`);
                await new Promise(resolve => setTimeout(resolve, delay));
                return fetchWithRetry(fn, retries - 1, delay * 1.5);
            }
            throw error;
        }
    };

    const simulateProgress = () => {
        setLoadingProgress(0);
        const interval = setInterval(() => {
            setLoadingProgress(prev => {
                if (prev >= 90) {
                    clearInterval(interval);
                    return 90;
                }
                return prev + 10;
            });
        }, 800);

        return () => clearInterval(interval);
    };

    const handleConfirmarPagamento = async (dados: IPagamentoData) => {
        let progressCleanup: (() => void) | null = null;

        try {
            // Validações iniciais
            if (cpfNaNotaInformado) {
                if (!dados.nome?.trim()) {
                    setTms('error');
                    setMsg('Nome do cliente é obrigatório');
                    setOpenmsg(true);
                    return;
                }

                if (!dados.cpf || !isValidCPF(dados.cpf)) {
                    setTms('error');
                    setMsg('CPF inválido');
                    setOpenmsg(true);
                    return;
                }
            }

            if (isPagamentoCartao) {
                if (!dados.tband || dados.tband === '') {
                    setTbandError('Bandeira é obrigatória');
                    setTms('error');
                    setMsg('Selecione a bandeira do cartão');
                    setOpenmsg(true);
                    return;
                }

                if (!dados.caut || dados.caut.length < 6) {
                    setCautError('Mínimo 6 dígitos');
                    setTms('error');
                    setMsg('CAUT deve ter no mínimo 6 dígitos');
                    setOpenmsg(true);
                    return;
                }
            }

            // Nova tentativa — some com o erro da tentativa anterior antes
            // de mostrar o loading.
            setErroEmissao('');

            // Mostrar loading de tela cheia
            setShowFullScreenLoading(true);
            setLoadingMessage('Validando dados da nota fiscal...');

            // Iniciar simulação de progresso
            progressCleanup = simulateProgress();

            const dadosParaEnviar = {
                ...dados,
                cpf: cpfNaNotaInformado ? dados.cpf.replace(/\D/g, '') : '',
                nome: cpfNaNotaInformado ? dados.nome : '',
            };

            const pagamentoSchema = createPagamentoSchema(cpfNaNotaInformado);
            const dadosValidados = await pagamentoSchema.validate(dadosParaEnviar, {
                abortEarly: false,
                stripUnknown: true,
            });

            if (!isMountedRef.current) return;

            setLoadingMessage('Enviando para SEFAZ...');
            setLoadingProgress(40);

            const dadosCompletos = {
                ...dadosValidados,
                idconta: '',
                faturado: true,
                tband: isPagamentoCartao ? dadosValidados.tband || '' : '',
                caut: isPagamentoCartao ? dadosValidados.caut || '000000' : '',
            };

            setLoadingMessage('Processando autorização da nota...');
            setLoadingProgress(60);

            // Função para chamar o serviço
            const callService = () => FaturamentoService.createnfce(dadosCompletos);

            // Executar com retry automático e timeout
            const timeoutPromise = new Promise((_, reject) => {
                timeoutRef.current = setTimeout(() => {
                    reject(new Error('timeout'));
                }, 60000); // 60 segundos de timeout
            });

            const resultPromise = fetchWithRetry(callService, 2, 2000);

            const resultado = await Promise.race([resultPromise, timeoutPromise]);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            if (!isMountedRef.current) return;

            if (!(resultado instanceof Blob)) {
                throw new Error('Arquivo da NFC-e inválido.');
            }

            setLoadingMessage('Nota autorizada! Preparando visualização...');
            setLoadingProgress(100);

            // Pequeno delay para o usuário ver o 100%
            await new Promise(resolve => setTimeout(resolve, 500));

            if (!isMountedRef.current) return;

            // Armazenar o blob do PDF
            setPdfBlob(resultado);

            // Esconder o loading de tela cheia
            setShowFullScreenLoading(false);

            // Mostrar a visualização de impressão
            setShowPrintPreview(true);

            // Mostrar mensagem de sucesso
            setTms('success');
            setMsg('NFC-e emitida com sucesso!');
            setOpenmsg(true);

        } catch (error: any) {
            console.error('Erro detalhado:', error);

            if (!isMountedRef.current) return;

            setShowFullScreenLoading(false);

            // Limpar timeout se existir
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Tratamento específico para timeout
            if (error?.message === 'timeout' || error?.message?.includes('timeout')) {
                setErroEmissao('Tempo limite excedido. O servidor está demorando para responder. Tente novamente.');
            }
            // Tratamento para erro de rede
            else if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
                setErroEmissao('Erro de conexão. Verifique sua internet e tente novamente.');
            }
            // Outros erros — mensagem exata que o backend/SEFAZ devolveu
            // (ver FaturamentoService.createnfce), sem reescrever pra algo
            // genérico.
            else {
                const errorMessage = error?.message || 'Erro ao gerar NFC-e. Verifique sua conexão e tente novamente.';
                setErroEmissao(error.name === 'ValidationError' ? error.errors[0] : errorMessage);
            }
        } finally {
            if (progressCleanup) {
                progressCleanup();
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    };
    handleConfirmarPagamentoRef.current = handleConfirmarPagamento;

    return (
        <>
            {/* Modal de Visualização de Impressão */}
            <PrintPreviewModal
                open={showPrintPreview}
                pdfBlob={pdfBlob}
                onClose={handleClosePrintPreview}
                onPrint={handlePrint}
            />

            <Dialog
                open={open && !showPrintPreview}
                onClose={handleCloseAttempt}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        position: 'relative',
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        overflow: 'hidden',
                    }
                }}
            >
                {/* Loading de Tela Cheia — ver comentário no componente
                    FullScreenLoading (topo do arquivo) sobre por que é
                    filho do Paper em vez de um Backdrop solto. */}
                {showFullScreenLoading && (
                    <FullScreenLoading
                        message={loadingMessage}
                        progress={loadingProgress}
                    />
                )}

                {isLoading && (
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            zIndex: 1
                        }}
                    />
                )}

                {/* Erro de EMITIR a nota (falha na SEFAZ/backend, ver catch
                    de handleConfirmarPagamento) — flutua por cima do próprio
                    diálogo (mesmo Paper, position:relative acima), sem
                    depender de Snackbar/portal separado, então não tem como
                    ficar atrás por z-index. Não mexe no layout (position:
                    absolute) — só esse erro específico abre assim; CPF/
                    bandeira/CAUT continuam no Snackbar de sempre. */}
                {erroEmissao && (
                    <Box sx={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 20 }}>
                        <Alert
                            severity="error"
                            variant="filled"
                            onClose={() => setErroEmissao('')}
                            sx={{ borderRadius: 2, boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}
                        >
                            {erroEmissao}
                        </Alert>
                    </Box>
                )}

                {/* Header — ícone + título/subtítulo à esquerda, número da
                    venda + valor total ao centro/direita (economiza espaço
                    — não precisam mais de uma faixa própria abaixo, ver
                    DialogContent) e X (36x36, circular) no canto. */}
                <Box sx={{
                    p: 2.5,
                    position: 'relative',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <ReceiptIcon sx={{
                            fontSize: 28,
                            color: colors.primarySolid,
                            flexShrink: 0,
                        }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight="700" color="text.primary" noWrap>
                                Emitir NFC-e
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                Preencha os dados de pagamento
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                Nº da venda
                            </Typography>
                            <Typography variant="body2" fontWeight={700} noWrap>
                                {vendaResumo?.numero || '—'}
                            </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                Valor total
                            </Typography>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: colors.primarySolid }}>
                                {vendaResumo
                                    ? `R$ ${vendaResumo.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : '—'}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseAttempt}
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: theme.palette.mode === 'light' ? '#F1F3F5' : 'rgba(255,255,255,0.08)',
                                color: 'text.secondary',
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent sx={{ p: 2.5 }}>
                    {/* Cliente (CPF+Nome) — número/valor da venda saíram
                        daqui e foram pro header acima. */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            bgcolor: corFundoSuave,
                            borderRadius: 3,
                            p: 2,
                            mb: 2.5,
                        }}
                    >
                        <AccountCircleOutlinedIcon sx={{ color: colors.primarySolid, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {!cpfNaNotaInformado ? (
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleAbrirCpfDialog}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        border: 'none',
                                        background: 'none',
                                        p: 0,
                                        cursor: 'pointer',
                                        color: COR_CONTRASTE_CAMPO,
                                        fontWeight: 700,
                                        fontSize: '0.8125rem',
                                    }}
                                >
                                    Adicionar CPF
                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700} noWrap>{pagamento.cpf}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                            {pagamento.nome}
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={handleAbrirCpfDialog} sx={{ ml: 'auto', flexShrink: 0 }}>
                                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Forma de Pagamento */}
                    <Paper elevation={0} sx={{
                        p: 2,
                        mb: 1,
                        bgcolor: corFundoCard,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                    }}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PaymentIcon fontSize="small" sx={{ color: colors.primarySolid }} />
                            Forma de Pagamento
                        </Typography>

                        <FormControl fullWidth>
                            <Select
                                value={pagamento.tpag}
                                onChange={(e) => {
                                    setPagamento({ ...pagamento, tpag: e.target.value, tband: '', caut: '' });
                                    setTbandError('');
                                    setCautError('');
                                }}
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: theme.palette.background.paper,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.divider,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.primarySolid,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.primarySolid,
                                        borderWidth: '1.5px',
                                    },
                                }}
                                renderValue={(selected) => {
                                    const selectedOption = todasFormasPagamento.find(f => f.id === selected);
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {selectedOption?.icon}
                                            <Typography>{selectedOption?.nome}</Typography>
                                        </Box>
                                    );
                                }}
                            >
                                {todasFormasPagamento.map((forma) => (
                                    <MenuItem key={forma.id} value={forma.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {forma.icon}
                                            <Typography>{forma.nome}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Paper>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2.5, px: 0.5, color: 'text.secondary' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                            {isOrcamento
                                ? 'Orçamento: Dinheiro sugerido — altere se necessário.'
                                : 'Selecione a forma de pagamento utilizada nesta venda.'}
                        </Typography>
                    </Box>

                    {/* Detalhes do Cartão */}
                    {isPagamentoCartao && (
                        <Paper elevation={0} sx={{
                            p: 2,
                            mb: 2.5,
                            bgcolor: corFundoCard,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`
                        }}>
                            <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CreditCardIcon fontSize="small" sx={{ color: colors.primarySolid }} />
                                Detalhes do Cartão
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id="tband-label" sx={{ color: tbandError ? undefined : COR_CONTRASTE_CAMPO, fontWeight: 600 }}>
                                            Bandeira do Cartão
                                        </InputLabel>
                                        <Select
                                            labelId="tband-label"
                                            value={pagamento.tband || ''}
                                            onChange={(e) => handleTbandChange(e.target.value)}
                                            label="Bandeira do Cartão"
                                            error={!!tbandError}
                                            sx={{
                                                borderRadius: 2,
                                                bgcolor: alpha(COR_CONTRASTE_CAMPO, 0.06),
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: tbandError ? undefined : alpha(COR_CONTRASTE_CAMPO, 0.7),
                                                    borderWidth: '1.5px',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: tbandError ? undefined : COR_CONTRASTE_CAMPO,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: tbandError ? undefined : COR_CONTRASTE_CAMPO,
                                                    borderWidth: '2px',
                                                },
                                            }}
                                        >
                                            <MenuItem value="">Selecione a bandeira</MenuItem>
                                            {bandeirasCartao.map((b) => (
                                                <MenuItem key={b.id} value={b.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            bgcolor: b.cor
                                                        }} />
                                                        <Typography>{b.nome}</Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {tbandError && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                {tbandError}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Código da Autorização (CAUT)"
                                        size="small"
                                        value={pagamento.caut}
                                        onChange={(e) => handleCautChange(e.target.value)}
                                        placeholder="Digite o código de autorização"
                                        error={!!cautError}
                                        helperText={cautError || "Código fornecido pela operadora do cartão"}
                                        InputLabelProps={{ sx: { color: cautError ? undefined : COR_CONTRASTE_CAMPO, fontWeight: 600 } }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: alpha(COR_CONTRASTE_CAMPO, 0.06),
                                                transition: 'all 0.2s',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: cautError ? undefined : alpha(COR_CONTRASTE_CAMPO, 0.7),
                                                    borderWidth: '1.5px',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: cautError ? undefined : COR_CONTRASTE_CAMPO,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: cautError ? undefined : COR_CONTRASTE_CAMPO,
                                                    borderWidth: '2px',
                                                },
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Promoção - card clicável (checkbox + tag + seta), só
                        pra pagamento em Dinheiro. */}
                    {isPagamentoDinheiro && (
                        <Paper
                            elevation={0}
                            onClick={() => setPagamento((prev) => ({ ...prev, promocao: !prev.promocao }))}
                            sx={{
                                p: 1.5,
                                bgcolor: corFundoCard,
                                borderRadius: 2,
                                border: `1px solid ${pagamento.promocao ? colors.primarySolid : theme.palette.divider}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                cursor: 'pointer',
                                transition: 'border-color 150ms ease',
                            }}
                        >
                            <Checkbox
                                checked={pagamento.promocao}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setPagamento({ ...pagamento, promocao: e.target.checked })}
                                sx={{
                                    p: 0.5,
                                    '& .MuiSvgIcon-root': { fontSize: 20 },
                                    '&.Mui-checked': { color: colors.primarySolid },
                                }}
                            />
                            <LocalOfferIcon fontSize="small" color="warning" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>Aplicar promoção especial</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Descontos e condições promocionais podem ser aplicados na emissão.
                                </Typography>
                            </Box>
                            <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
                        </Paper>
                    )}

                    {/* Dica de teclado */}
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        fontSize: '0.75rem',
                        color: 'text.secondary'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <kbd style={{
                                padding: '2px 6px',
                                backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#333',
                                borderRadius: 3,
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                                border: `1px solid ${theme.palette.divider}`
                            }}>F12</kbd>
                            <span>Emitir NFC-e</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <kbd style={{
                                padding: '2px 6px',
                                backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#333',
                                borderRadius: 3,
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                                border: `1px solid ${theme.palette.divider}`
                            }}>ESC</kbd>
                            <span>Fechar</span>
                        </Box>
                    </Box>
                </DialogContent>

                <Divider sx={{ mx: 2.5 }} />

                <DialogActions sx={{ p: 2.5 }}>
                    <Button
                        onClick={() => handleConfirmarPagamento(pagamento)}
                        disabled={isLoading}
                        variant="contained"
                        fullWidth
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            height: 56,
                            background: colors.primaryGradient,
                            color: colors.textOnPrimary,
                            '&:hover': {
                                background: colors.hoverGradient,
                            }
                        }}
                        startIcon={<ReceiptIcon />}
                    >
                        Emitir NFC-e (F12)
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog "Informar CPF do cliente" */}
            <Dialog
                open={openCpfDialog}
                onClose={handleCancelarCpfDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <Box sx={{
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Informar CPF do cliente
                    </Typography>
                    <IconButton size="small" onClick={handleCancelarCpfDialog} sx={{ color: 'text.secondary' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        label="CPF"
                        size="small"
                        value={cpfDraft}
                        onChange={(e) => handleCpfDraftChange(e.target.value)}
                        placeholder="000.000.000-00"
                        error={!!cpfDraftError}
                        helperText={cpfDraftError}
                        inputProps={{ maxLength: 14 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSalvarCpfDialog(); }}
                    />
                    <TextField
                        fullWidth
                        required
                        label="Nome / Razão Social"
                        size="small"
                        value={nomeDraft}
                        onChange={(e) => { setNomeDraft(e.target.value); if (nomeDraftError) setNomeDraftError(''); }}
                        placeholder="Nome ou razão social do cliente"
                        error={!!nomeDraftError}
                        helperText={nomeDraftError}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSalvarCpfDialog(); }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
                    <Button
                        onClick={handleCancelarCpfDialog}
                        variant="outlined"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSalvarCpfDialog}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            background: colors.primaryGradient,
                            color: colors.textOnPrimary,
                            '&:hover': { background: colors.hoverGradient },
                        }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={showCloseConfirmation}
                onClose={handleCancelClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ pb: 1, pt: 2 }}>
                    <Typography variant="h6" fontWeight="600">
                        Sair da emissão?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Tem certeza que deseja cancelar a emissão da NFC-e? Os dados preenchidos serão perdidos.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button
                        onClick={handleCancelClose}
                        variant="outlined"
                        sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}
                    >
                        Continuar
                    </Button>
                    <Button
                        onClick={handleConfirmClose}
                        variant="contained"
                        color="error"
                        sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}
                    >
                        Sair
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar flutuante, na frente do diálogo (z-index de Snackbar
                já fica acima de Dialog no MUI) — não mexe no layout da tela
                de emitir nota, só sobrepõe. Erro não some sozinho (ver
                autoHideDuration abaixo e handleCloseMsg, que ignora
                clickaway) — só fecha clicando no X. */}
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={openmsg && !sucessoNoHeader}
                autoHideDuration={tms === 'error' ? null : 6000}
                onClose={handleCloseMsg}
                style={{ zIndex: 999999 }}
                sx={{ zIndex: 999999 }}
            >
                <Alert
                    onClose={handleCloseMsg}
                    severity={tms}
                    variant="filled"
                    sx={{ borderRadius: 2 }}
                >
                    {msg}
                </Alert>
            </Snackbar>
        </>
    );
};
